/**
 * Playground snippet persistence.
 * Storage: users/{userId}/playgroundSnippets/{snippetId}
 *
 * Writes should be intentional or debounced — never on every keystroke from callers.
 */

import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  setDoc,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { ensureFirestoreAuthReady } from '../utils/firestoreAuthReady';
import { normalizePlaygroundLanguage } from '../components/Playground/playgroundLanguages';
import {
  normalizeSnippetTitle,
  PLAYGROUND_UNTITLED_SNIPPET_TITLE,
} from '../components/Playground/playgroundSnippets';

export const PLAYGROUND_SNIPPETS_COLLECTION = 'playgroundSnippets';
export const PLAYGROUND_SNIPPET_MAX_CODE_CHARS = 100_000;
const BATCH_LIMIT = 400;

function playgroundSnippetsRef(userId) {
  return collection(db, 'users', userId, PLAYGROUND_SNIPPETS_COLLECTION);
}

function playgroundSnippetRef(userId, snippetId) {
  return doc(db, 'users', userId, PLAYGROUND_SNIPPETS_COLLECTION, snippetId);
}

function toMillis(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value && typeof value.toMillis === 'function') return value.toMillis();
  if (value instanceof Date) return value.getTime();
  return Date.now();
}

export function serializePlaygroundSnippet(snippet) {
  const code = typeof snippet.code === 'string' ? snippet.code : '';
  return {
    title: normalizeSnippetTitle(snippet.title, PLAYGROUND_UNTITLED_SNIPPET_TITLE),
    language: normalizePlaygroundLanguage(snippet.language),
    code: code.slice(0, PLAYGROUND_SNIPPET_MAX_CODE_CHARS),
    createdAt: toMillis(snippet.createdAt),
    updatedAt: toMillis(snippet.updatedAt),
  };
}

export function deserializePlaygroundSnippet(id, data = {}) {
  return {
    id,
    title: normalizeSnippetTitle(data.title, PLAYGROUND_UNTITLED_SNIPPET_TITLE),
    language: normalizePlaygroundLanguage(data.language),
    code: typeof data.code === 'string' ? data.code : '',
    createdAt: toMillis(data.createdAt),
    updatedAt: toMillis(data.updatedAt || data.createdAt),
  };
}

/**
 * Merge local + remote by id, keeping the newer updatedAt.
 * When timestamps match, prefer remote (source of truth after sync).
 */
export function mergePlaygroundSnippets(localSnippets = [], remoteSnippets = []) {
  const byId = new Map();

  for (const snippet of localSnippets) {
    if (!snippet?.id) continue;
    byId.set(snippet.id, snippet);
  }

  for (const remote of remoteSnippets) {
    if (!remote?.id) continue;
    const local = byId.get(remote.id);
    if (!local || remote.updatedAt >= local.updatedAt) {
      byId.set(remote.id, remote);
    }
  }

  return Array.from(byId.values()).sort((a, b) => b.updatedAt - a.updatedAt);
}

/** Snippets that need uploading because they are missing remotely or newer locally. */
export function snippetsNeedingRemoteUpsert(mergedSnippets, remoteSnippets = []) {
  const remoteById = new Map(remoteSnippets.map((item) => [item.id, item]));

  return mergedSnippets.filter((snippet) => {
    const remote = remoteById.get(snippet.id);
    return !remote || snippet.updatedAt > remote.updatedAt;
  });
}

export async function listPlaygroundSnippets(userId) {
  if (!userId) return [];

  const ready = await ensureFirestoreAuthReady(userId);
  if (!ready) return [];

  const snap = await getDocs(playgroundSnippetsRef(userId));
  return snap.docs
    .map((item) => deserializePlaygroundSnippet(item.id, item.data()))
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function upsertPlaygroundSnippet(userId, snippet) {
  if (!userId || !snippet?.id) return;

  const ready = await ensureFirestoreAuthReady(userId);
  if (!ready) return;

  await setDoc(playgroundSnippetRef(userId, snippet.id), serializePlaygroundSnippet(snippet), {
    merge: true,
  });
}

export async function upsertPlaygroundSnippetsBatch(userId, snippets = []) {
  if (!userId || !snippets.length) return;

  const ready = await ensureFirestoreAuthReady(userId);
  if (!ready) return;

  for (let i = 0; i < snippets.length; i += BATCH_LIMIT) {
    const chunk = snippets.slice(i, i + BATCH_LIMIT);
    const batch = writeBatch(db);
    chunk.forEach((snippet) => {
      if (!snippet?.id) return;
      batch.set(
        playgroundSnippetRef(userId, snippet.id),
        serializePlaygroundSnippet(snippet),
        { merge: true }
      );
    });
    await batch.commit();
  }
}

export async function deletePlaygroundSnippetRemote(userId, snippetId) {
  if (!userId || !snippetId) return;

  const ready = await ensureFirestoreAuthReady(userId);
  if (!ready) return;

  await deleteDoc(playgroundSnippetRef(userId, snippetId));
}
